// External libs
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faLightbulb,
  faTrash,
  faPowerOff
} from "@fortawesome/free-solid-svg-icons";

import axiosInstance from '../utils/axiosInstance.js';

// popupPage 
import DeviceEditPopupForm from "../components/popupPage/DeviceEditPopUp.js";
import DeviceUpdateFirmwarePopupForm from "../components/popupPage/DeviceFirmwarePopUp.js";
import FirmwareUploadPopupForm from "../components/popupPage/FirmwareUploadPopUp.js";
import FirmwareDeletePopupForm from "../components/popupPage/FirmwareDeletePopup.js";

// backend services
import {route_device_types, backend_request_interval_ms } from "../constants/BackendRoutes.js";
import { fetchDeviceTableData } from "../services/deviceService.js";

// main page componets
import WidgetTable from "../components/WidgetTable.js"

// utils
import { makeIdentifyToast } from "../utils/utils.js";

// mqtt services
import { flashLED, rebootDevice } from "../services/mqttService.js";

/**
 * A summary page card to display which use the WidgetTextBoxLarge to displays multiple rows of data for a device
 * 
 * @param {object} devObj - device Object from the backend
 *
 * @returns {JSX.Element} The rendered device summary page
 *
 * @example
 * <DeviceListPanel devObj="xxx" />
 */
const DeviceListPanel = ({
  onSelectedDevObj
     }) => {

  // Device selected by user, support multiple selection
  const [selectedDeviceId, setSelectedDeviceId]= useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  
  // Device data queried from backend
  const [devices, setDevices] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);

  const [openPopup, setOpenPopup] = useState(null);
  const closePopup = () => setOpenPopup(null);

  const [popUpData, setPopUpData] = useState({});

  const [showDialogId, setShowDialogId] = useState("");

  useEffect(() => {
    let timer;
    let stopped = false;

    const fetchDeviceLoop = async() => {
      await getDeviceTableData();
      if (!stopped) {
        timer = setTimeout(fetchDeviceLoop, backend_request_interval_ms);
      }
    }

    fetchDeviceLoop();

    return () => {
      stopped = true;
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    };
  }, []);

  const getDeviceTableData = async () => {
    try {
      // Fetch devices from your backend
      const deviceJson = await fetchDeviceTableData();
      setDevices(deviceJson);
    } catch (error) {
      console.error("Error fetching Device List:", error);
      setDevices([]);
    }
  };

  useEffect(() => {
    const fetchDeviceTypes = async () => {
      try {
        // Fetch device types from your backend
        console.log(route_device_types);
        const response = await axiosInstance.get(route_device_types);
        setDeviceTypes(response.data);
      } catch (error) {
        if ('status' in error)
        {
          console.log(`Backend not responding: ${error.status} error`);
        }
        else
        {
          console.error("Error fetching Device Types List:", error);
        }
      }
    };
    fetchDeviceTypes();
  }, []);


  useEffect(() => {
    if (!devices)
    {
      return;
    }
    const selectedObj = devices.find((obj) => obj.id === selectedDeviceId);
    onSelectedDevObj(selectedObj);
  }, [selectedDeviceId, devices]);    

  // Handle form submission
  const handleFormSubmit = (formData) => {};
 
  // Handle device selection
  const handleDeviceSelection = (id) => {
    if (id != selectedDeviceId) {
      setSelectedDeviceId(id);
    } else {      
      setSelectedDeviceId("");
    }
  };

  const handleDeviceEditPopupClose = async () => {
    try {
      await getDeviceTableData();
    } catch (err) {
      console.error(err);
    } finally {
      setOpenPopup(null);
    }
  };

  const handleDeviceFirmwareUpdateSelection = (devices) => {
    const device = devices[0];
    if (!device || !device.name || !device.deviceType || !device.firmware) {
      console.error("Invalid device object:", device);
      return;
    }
    if (!device || typeof device !== "object") {
      console.error("Invalid device object:", device);
      return;
    }
    setSelectedDevices([...devices]);
    setOpenPopup("firmwareUpdate");
  };

  const toggleDialog = (id) => {
    if (showDialogId != id) {
      setShowDialogId(id);
    } else {
      setShowDialogId(null);
    }
  };

  // This function used for Add/Edit Device popup form
  const handleDeviceEditSelection = (devices, title, submitType) => {
    let device;
    if (devices == null) {
      device = null;
    } else {
      device = devices[0];
    }
    setPopUpData({
      device: device,
      title: title,
      submitType: submitType,
    });
    // Open the popup for adding or editing a device
    // submitType is used for differentiation (ToDo: why use submitType?)
    setOpenPopup("deviceAddOrEdit");
  };

  const onDialogClick = (text, devices) => {
    if (text === "edit") {
      handleDeviceEditSelection(devices, "Edit Device", "update");
    } else if (text === "identify") {
      devices.forEach((device) => {
        flashLED(device.id);
        makeIdentifyToast(device.id, "LED will flash for 15 seconds", faLightbulb);
      });
    } else if (text === "firmware") {
      handleDeviceFirmwareUpdateSelection(devices);
    }
    else if (text === "reboot") {
      devices.forEach((device) => {
        rebootDevice(device.id);
        makeIdentifyToast(device.id, "device will reboot", faPowerOff);
      })
    }
    toggleDialog(null);
  };

  return (   
   <div className="device-list-panel">
          {/* popup box display control */}
          <div>
                {openPopup === "deviceAddOrEdit" && (
                  <DeviceEditPopupForm
                    onClose={handleDeviceEditPopupClose}
                    onSubmit={handleFormSubmit}
                    deviceTypes={deviceTypes}
                    inputData={popUpData}
                  />
                )}

                {openPopup === "firmwareUpdate" && (
                  <DeviceUpdateFirmwarePopupForm
                    onClose={closePopup}
                    onSubmit={handleFormSubmit}
                    devices={selectedDevices}
                  />
                )}

                {openPopup === "firmwareUpload" && (
                  <FirmwareUploadPopupForm
                    onClose={closePopup}
                    onSubmit={handleFormSubmit}
                    deviceTypes={deviceTypes}
                  />
                )}

                {openPopup === "firmwareDelete" && (
                  <FirmwareDeletePopupForm
                    onClose={closePopup}
                    onSubmit={handleFormSubmit}
                    deviceTypes={deviceTypes}
                  />
                )}

          </div>

          {/*Device Selection Table */}
          <div className="widget-box widget-box-1">
            <h3>Manage Your Fleet</h3>
            {/* Firmware management button */}
            <div className="widget-btns">
              <button
                onClick={() =>
                  handleDeviceEditSelection(
                    { name: "", type: deviceTypes[0], heartbeat_int: 60 },
                    "Add New Device",
                    "add",
                  )
                }
                class="submit-btn"
              >
                <FontAwesomeIcon icon={faPlus} /> Add Device
              </button>
              <button
                onClick={() => setOpenPopup("firmwareUpload")}
                className="submit-btn"
              >
                <FontAwesomeIcon icon={faPlus} /> Upload Firmware
              </button>
              <button
                onClick={() => setOpenPopup("firmwareDelete")}
                className="submit-btn"
              >
                <FontAwesomeIcon icon={faTrash} /> Delete Firmware
              </button>
            </div>

            {/* Device List Table */}
            <WidgetTable
              devices={devices}
              selectedDeviceId={selectedDeviceId}
              handleDeviceSelection={handleDeviceSelection}
              onDialogClick={onDialogClick}
              toggleDialog={toggleDialog}
              showDialogId={showDialogId}
            ></WidgetTable>
          </div>
        </div>
  );
};

export default DeviceListPanel;